import { useState, useEffect, useCallback, useRef } from "react";
import { supabase , authHeader} from "./supabase";
import { subirDatos, eliminarDato } from "./syncSupabase";
import ExcelJS from "exceljs";

const STORAGE_EMPRESAS = "alqos_legalmatrix_empresas";
const RUBROS_LM = ["Gráfica / Imprenta","Alimentaria","Química","Metalmecánica","Textil","Construcción","Logística","Comercio","Salud","Otro"];
const PROVINCIAS_LM = ["Buenos Aires","CABA","Córdoba","Santa Fe","Mendoza","Tucumán","Salta","Otro"];

// Checkboxes de riesgo del perfil de empresa (Fase 3 del plan del motor regulatorio).
// Cada uno activa uno o más aspectos físicos (los que clasificamos hoy en hs_normativa_base).
const ASPECTOS_RIESGO = [
  { key: "efluentes",         label: "💧 Efluentes líquidos (vertidos, desagües)",         ambiental: ["Efluentes Líquidos"] },
  { key: "emisiones",         label: "🌫 Emisiones atmosféricas (gases, calderas)",         ambiental: ["Emisiones Atmosféricas"] },
  { key: "residuosPeligrosos",label: "☣️ Residuos peligrosos / especiales",                 ambiental: ["Residuos Peligrosos/Especiales"] },
  { key: "residuosPatogenicos",label:"🩸 Residuos patogénicos",                             ambiental: ["Residuos Patogénicos"] },
  { key: "ruido",              label:"🔊 Ruido / vibraciones",                              ambiental: ["Ruido y Vibraciones"], sst: ["Ruido y Vibraciones (ocupacional)"] },
  { key: "agua",               label:"🚰 Consumo alto de agua",                             ambiental: ["Consumo de Agua"] },
  { key: "energia",            label:"⚡ Consumo alto de energía",                          ambiental: ["Consumo de Energía"] },
  { key: "quimicos",           label:"🧪 Sustancias químicas peligrosas / solventes",       ambiental: ["Sustancias Químicas Peligrosas"], sst: ["Sustancias Peligrosas (exposición laboral)"] },
  { key: "suelo",              label:"🏔 Riesgo de contaminación de suelo",                 ambiental: ["Contaminación de Suelo"] },
  { key: "transporteMerc",     label:"🚚 Transporte de mercancías peligrosas",              ambiental: ["Transporte de Mercancías Peligrosas"] },
  { key: "electrico",          label:"🔌 Instalación / riesgo eléctrico relevante",         sst: ["Riesgo Eléctrico"] },
  { key: "altura",             label:"🪜 Trabajo en altura",                                sst: ["Trabajo en Altura"] },
  { key: "confinados",         label:"🕳 Espacios confinados",                              sst: ["Espacios Confinados"] },
  { key: "incendio",           label:"🔥 Riesgo de incendio/explosión (calderas, presión)", sst: ["Incendios y Explosiones"] },
  { key: "maquinaria",         label:"⚙️ Maquinaria pesada / herramientas de riesgo",       sst: ["Máquinas y Herramientas"] },
];

const NIVELES = [
  { key: "nacional", label: "🇦🇷 Nacional", jurisdiccion: ["nacional"], color: "#7B5BF2" },
  { key: "provincial", label: "🏛 Provincial", jurisdiccion: ["provincial"], color: "#2A7FFF" },
  { key: "municipal", label: "🏙 Municipal", jurisdiccion: ["municipal"], color: "#16a34a" },
];

function repararJSON(texto) {
  try {
    const first = texto.indexOf("{");
    const last = texto.lastIndexOf("}");
    if (first === -1 || last === -1) return null;
    return JSON.parse(texto.slice(first, last + 1));
  } catch {
    return null;
  }
}

export default function LegalMatrixEngine({ onVolver, oscuro, usuario }) {
  const [empresas, setEmpresas] = useState([]);
  const [empresaSel, setEmpresaSel] = useState(null);
  const [cargandoEmpresas, setCargandoEmpresas] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [orgIdsUsuario, setOrgIdsUsuario] = useState(null);
  const [vista, setVista] = useState("lista");
  const [editandoEmpresaLM, setEditandoEmpresaLM] = useState(null);
  const [formEmpresaLM, setFormEmpresaLM] = useState({ rubro: "Metalmecánica", sitios: [] });

  const [resultados, setResultados] = useState({ nacional: null, provincial: null, municipal: null });
  const [analizando, setAnalizando] = useState(false);
  const [progreso, setProgreso] = useState("");
  const [actualizando, setActualizando] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [historial, setHistorial] = useState({ nacional: null, provincial: null, municipal: null });
  const [clasificando, setClasificando] = useState(false);
  const [progresoClasificacion, setProgresoClasificacion] = useState("");
  const [mantenimiento, setMantenimiento] = useState(false);
  const [progresoMantenimiento, setProgresoMantenimiento] = useState("");
  const [pendientesMantenimiento, setPendientesMantenimiento] = useState({ nacional: 0, provincial: 0, municipal: 0 });
  const [cargandoGratis, setCargandoGratis] = useState(false);
  const cancelarAnalisisRef = useRef(false);
  const [cancelandoAnalisis, setCancelandoAnalisis] = useState(false);
  const abortVigenciasRef = useRef(null);

  const bg = oscuro ? "#09184D" : "#F0EEFF";
  const cardBg = oscuro ? "#0d2160" : "white";
  const texto = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde = oscuro ? "#1e3a8a" : "#eee";
  const navBg = oscuro ? "#060f30" : "#09184D";
  const subtexto = oscuro ? "#9aabd4" : "#808085";
  const card = { background: cardBg, borderRadius: "12px", padding: "1.2rem 1.5rem", border: `1px solid ${borde}`, marginBottom: "12px" };
  const inp = { width:"100%", padding:"8px 12px", borderRadius:"8px", border:`1px solid ${borde}`, fontSize:"13px", boxSizing:"border-box", marginTop:"4px", background:cardBg, color:texto };
  const lbl = { fontSize:"12px", color:subtexto, fontWeight:"600", display:"block", marginTop:"12px" };

  useEffect(() => {
    if (!usuario?.id) return;
    supabase.from("usuario_organizaciones").select("organizacion_id").eq("usuario_id", usuario.id)
      .then(({ data }) => setOrgIdsUsuario((data || []).map(r => r.organizacion_id)));
  }, [usuario?.id]);

  // Filtra server-side por organizacion_id para usuarios no-Administrador — antes traía la
  // tabla completa (bajarDatos, sin WHERE) y filtraba recién en el render (ver empresasVisibles
  // más abajo). Espera a conocer orgIdsUsuario antes de consultar para no traer de más mientras
  // tanto.
  useEffect(() => {
    const esAdministrador = usuario?.rol === "Administrador";
    if (!esAdministrador && orgIdsUsuario === null) return;
    const cargar = async () => {
      setCargandoEmpresas(true);
      try {
        let query = supabase.from("hs_legalmatrix_empresas").select("data");
        if (!esAdministrador) query = query.in("data->>organizacion_id", (orgIdsUsuario || []).map(String));
        const { data, error } = await query;
        if (error) throw error;
        const lista = (data || []).map(r => r.data);
        localStorage.setItem(STORAGE_EMPRESAS, JSON.stringify(lista));
        setEmpresas(lista);
      } catch (e) {
        console.error(e);
        try { setEmpresas(JSON.parse(localStorage.getItem(STORAGE_EMPRESAS) || "[]")); } catch { setEmpresas([]); }
      }
      setCargandoEmpresas(false);
    };
    cargar();
  }, [usuario?.rol, orgIdsUsuario]);

  const guardarEmpresasLM = (lista) => {
    localStorage.setItem(STORAGE_EMPRESAS, JSON.stringify(lista));
    setEmpresas(lista);
    subirDatos(STORAGE_EMPRESAS);
  };

  const cargarHistorial = useCallback(async (empresaId) => {
    const nuevos = {};
    for (const n of NIVELES) {
      const { data } = await supabase
        .from("hs_legalmatrix_resultados")
        .select("*")
        .eq("empresa_id", empresaId)
        .eq("nivel", n.key)
        .order("fecha_analisis", { ascending: false })
        .limit(1);
      if (data && data.length > 0) {
        nuevos[n.key] = data[0];
        setResultados(r => ({ ...r, [n.key]: data[0].data }));
      }
    }
    setHistorial(nuevos);
  }, []);

  const seleccionarEmpresa = (e) => {
    setResultados({ nacional: null, provincial: null, municipal: null });
    setEmpresaSel(e);
    cargarHistorial(e.id);
  };

  const guardarResultado = async (nivel, data) => {
    try {
      await supabase.from("hs_legalmatrix_resultados").insert({
        id: Math.floor(Date.now()),
        empresa_id: empresaSel.id,
        nivel,
        data,
        tenant_id: Number(localStorage.getItem("alqos_tenant_id")) || null,
      });
    } catch (e) { console.error("Error guardando resultado LegalMatrix:", e); }
  };

  const BATCH_SIZE = 12;
  const chunk = (arr, size) => {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  };

 // "Documental / Transversal" y "General SST" siempre quedan activos: son obligaciones
  // administrativas que no dependen de un aspecto físico puntual, aplican siempre.
  const calcularAspectosActivos = () => {
    const ambiental = new Set(["Documental / Transversal"]);
    const sst = new Set(["General SST", "Accidentes y Enfermedades del Trabajo"]);
    const marcados = empresaSel.aspectosRiesgo || {};
    ASPECTOS_RIESGO.forEach(a => {
      if (!marcados[a.key]) return;
      (a.ambiental || []).forEach(t => ambiental.add(t));
      (a.sst || []).forEach(t => sst.add(t));
    });
    return { ambiental, sst };
  };

  const obtenerCandidatas = async (nivelDef) => {
    const municipiosSitios = (empresaSel.sitios || []).map(s => s.municipio).filter(Boolean);
    const municipiosTodos = [empresaSel.municipio, ...municipiosSitios].filter(Boolean);
    const SELECT_CANDIDATAS = "codigo, descripcion, tipo, aplicacion, jurisdiccion, provincia, municipio, categoria, rubros_aplicables, aspectos_ambientales, aspectos_sst, enriquecimiento, enriquecimiento_fecha";

    let query = supabase
      .from("hs_normativa_base")
      .select(SELECT_CANDIDATAS)
      .eq("vigente", true)
      .eq("documento_cliente", false)
      .or("es_enunciativa.eq.false,es_enunciativa.is.null")
      .in("jurisdiccion", nivelDef.jurisdiccion);

    if (nivelDef.key === "provincial") query = query.eq("provincia", empresaSel.provincia || "");
    if (nivelDef.key === "municipal") query = query.in("municipio", municipiosTodos.length ? municipiosTodos : ["__nada__"]);

    const [porRubro, porTodos] = await Promise.all([
      query.filter("rubros_aplicables", "cs", `{"${empresaSel.rubro}"}`),
      supabase.from("hs_normativa_base").select(SELECT_CANDIDATAS)
        .eq("vigente", true).eq("documento_cliente", false)
        .or("es_enunciativa.eq.false,es_enunciativa.is.null")
        .in("jurisdiccion", nivelDef.jurisdiccion)
        .filter("rubros_aplicables", "cs", `{"Todos"}`),
    ]);

    if (porRubro.error || porTodos.error) {
      console.error(`Error consultando base (${nivelDef.key}):`, porRubro.error || porTodos.error);
      throw new Error(`No se pudo consultar la base normativa (${nivelDef.label}).`);
    }

    const combinadas = Array.from(new Map([...(porRubro.data || []), ...(porTodos.data || [])].map(n => [n.codigo, n])).values());

    // Filtro fino por aspecto físico (Fase 3): una norma Ambiental o SST solo entra si algún
    // aspecto que declara coincide con lo que la empresa marcó. Si la norma no tiene aspecto
    // clasificado (columna vacía), no se descarta por las dudas.
    const { ambiental: aspAmbActivos, sst: aspSstActivos } = calcularAspectosActivos();
    return combinadas.filter(n => {
      if (n.categoria === "Ambiental") {
        const tags = n.aspectos_ambientales || [];
        if (tags.length === 0) return true;
        return tags.some(t => aspAmbActivos.has(t));
      }
      if (n.categoria === "SST") {
        const tags = n.aspectos_sst || [];
        if (tags.length === 0) return true;
        return tags.some(t => aspSstActivos.has(t));
      }
      return true;
    });
  };


  // Campos que son propiedad de la NORMA (no cambian entre clientes) — se cachean en hs_normativa_base.
  const CAMPOS_ENRIQUECIMIENTO = ["nombre", "categoria", "aspecto", "area_responsable", "descripcion", "autoridad_aplicacion", "articulo_aplicable", "obligaciones_concretas", "evidencia_objetiva", "frecuencia", "vencimiento", "responsable_sugerido", "sanciones", "iso_clausula", "link", "actualizacion"];
  const CACHE_DIAS = 90;

  const esFresco = (n) => {
    if (!n.enriquecimiento || !n.enriquecimiento_fecha) return false;
    const dias = (Date.now() - new Date(n.enriquecimiento_fecha).getTime()) / (1000 * 60 * 60 * 24);
    return dias <= CACHE_DIAS;
  };

  const guardarEnriquecimiento = async (normativa) => {
    const updates = normativa.map(n => {
      const payload = {};
      CAMPOS_ENRIQUECIMIENTO.forEach(c => { if (n[c] !== undefined) payload[c] = n[c]; });
      return supabase.from("hs_normativa_base")
        .update({ enriquecimiento: payload, enriquecimiento_fecha: new Date().toISOString() })
        .eq("codigo", n.codigo);
    });
    try { await Promise.all(updates); } catch (e) { console.error("Error guardando enriquecimiento (no bloqueante):", e); }
  };

  // ── MANTENIMIENTO DEL CORPUS (admin, con web_search, cuesta API) ──
  // Genérico, sin empresa — enriquece la norma en sí, no evalúa aplicabilidad para nadie puntual.
  // Solo esto puede disparar gasto real. La atención al cliente (más abajo) nunca llama esto.
  const CAMPOS_MANTENIMIENTO = `{ "codigo":"...", ${CAMPOS_ENRIQUECIMIENTO.map(c => `"${c}":"..."`).join(", ")} }`;

  const mantenerLote = async (lote) => {
    const corpusTexto = lote.map(n => `${n.codigo} | ${n.descripcion} | tipo:${n.tipo || "-"} | aplicacion:${n.aplicacion || "-"} | jurisdiccion:${n.jurisdiccion || "-"}`).join("\n");

    const resp = await fetch("/api/aria", {
      method: "POST",
      headers: { ...(await authHeader()), "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-6",
        max_tokens: 10000,
        system: "Sos un consultor experto en Higiene y Seguridad y gestión ambiental argentina. Respondés SIEMPRE y ÚNICAMENTE con JSON válido, sin texto adicional, sin markdown.",
        tools: [{ type: "web_search_20250305", name: "web_search", max_uses: 10 }],
        messages: [{ role: "user", content: `Verificá con web_search si estas normas siguen vigentes (¿derogadas? ¿modificadas después de 2025?) y completá los campos. Esto es mantenimiento general del corpus, no es para una empresa puntual — no evalúes aplicabilidad. No inventes normas adicionales, usá SOLO las que están abajo. Respondé ÚNICAMENTE con JSON válido:

{ "normativa": [ ${CAMPOS_MANTENIMIENTO} ] }

NORMAS A VERIFICAR:
${corpusTexto}` }],
      }),
    });
    const respData = await resp.json();
    const texto2 = (respData.content || []).filter(b => b.type === "text").map(b => b.text).join("\n");
    const parsed = repararJSON(texto2);

    if (!parsed || !Array.isArray(parsed.normativa)) {
      throw new Error("ARIA no devolvió JSON válido para un lote de mantenimiento.");
    }
    await guardarEnriquecimiento(parsed.normativa);
    return parsed.normativa;
  };

  const mantenimientoCorpus = async () => {
    setMantenimiento(true);
    try {
      setProgresoMantenimiento("Buscando normas que necesitan mantenimiento...");
      const { data, error } = await supabase
        .from("hs_normativa_base")
        .select("codigo, descripcion, tipo, aplicacion, jurisdiccion, enriquecimiento, enriquecimiento_fecha")
        .eq("vigente", true)
        .eq("documento_cliente", false)
        .or("es_enunciativa.eq.false,es_enunciativa.is.null");

      if (error) throw error;
      const pendientes = (data || []).filter(n => !esFresco(n));
      if (pendientes.length === 0) {
        alert("No hay normas pendientes de mantenimiento — el corpus está al día.");
        return;
      }

      const lotes = chunk(pendientes, BATCH_SIZE);
      let ok = 0, fallidos = 0;
      for (let i = 0; i < lotes.length; i++) {
        setProgresoMantenimiento(`Tanda ${i + 1}/${lotes.length} (${lotes[i].length} normas) — podés cerrar esta pestaña en cualquier momento, lo hecho queda guardado`);
        try {
          const res = await mantenerLote(lotes[i]);
          ok += res.length;
        } catch (eLote) {
          console.error(`Error en mantenimiento tanda ${i + 1}:`, eLote);
          fallidos += lotes[i].length;
        }
      }
      alert(`Mantenimiento terminado: ${ok} normas actualizadas${fallidos ? `, ${fallidos} quedaron pendientes (volvé a apretar para reintentar)` : ""}.`);
    } catch (e) {
      console.error("Error en mantenimientoCorpus:", e);
      alert("Error en el mantenimiento del corpus. Revisá la consola.");
    } finally {
      setProgresoMantenimiento("");
      setMantenimiento(false);
    }
  };

  // Para normas ya enriquecidas por otro cliente (caché fresca): sin web_search, solo evalúa
  // aplicabilidad/riesgo para ESTA empresa puntual reusando los campos ya generados.
  const evaluarAplicabilidadLote = async (nivelDef, lote) => {
    const datosEmpresa = `Empresa: ${empresaSel.nombre}
Rubro: ${empresaSel.rubro || "sin dato"}
Empleados: ${empresaSel.empleados || "sin dato"}
Provincia: ${empresaSel.provincia || "sin dato"}
Municipio/Partido: ${empresaSel.municipio || "sin dato"}`;

    const corpusTexto = lote.map(n => `${n.codigo} | ${n.enriquecimiento?.nombre || n.descripcion} | ${n.enriquecimiento?.obligaciones_concretas || "-"}`).join("\n");

    const resp = await fetch("/api/aria", {
      method: "POST",
      headers: { ...(await authHeader()), "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-6",
        max_tokens: 6000,
        system: "Sos un consultor experto en Higiene y Seguridad y gestión ambiental argentina. Estas normas ya fueron verificadas y enriquecidas antes — solo evaluás si aplican a la empresa indicada. Respondés SIEMPRE y ÚNICAMENTE con JSON válido.",
        messages: [{ role: "user", content: `Para cada norma, evaluá si aplica a esta empresa puntual. Respondé ÚNICAMENTE con JSON válido:

{"evaluacion":[{"codigo":"...","aplicabilidad":"APLICA|NO APLICA|APLICA PARCIALMENTE","justificacion_aplicabilidad":"...","riesgo":"CRITICO|ALTO|MEDIO|BAJO"}]}

NORMAS (ya verificadas):
${corpusTexto}

${datosEmpresa}` }],
      }),
    });
    const respData = await resp.json();
    const texto2 = (respData.content || []).filter(b => b.type === "text").map(b => b.text).join("\n");
    const parsed = repararJSON(texto2);
    if (!parsed || !Array.isArray(parsed.evaluacion)) {
      throw new Error(`ARIA no devolvió JSON válido evaluando aplicabilidad (${nivelDef.label}).`);
    }
    const porCodigo = new Map(parsed.evaluacion.map(e => [String(e.codigo), e]));
    return lote.map(n => {
      const ev = porCodigo.get(String(n.codigo)) || {};
      return { codigo: n.codigo, ...(n.enriquecimiento || {}), ...ev };
    });
  };

  // Atención al cliente: SOLO lee lo ya mantenido (caché fresca). Nunca llama web_search,
  // así que nunca gasta API. Lo que no está mantenido se reporta como pendiente, no se inventa.
  const analizarTodo = async () => {
    if (!empresaSel) return;
    setAnalizando(true);
    cancelarAnalisisRef.current = false;
    setCancelandoAnalisis(false);
    setResultados({ nacional: null, provincial: null, municipal: null });
    setPendientesMantenimiento({ nacional: 0, provincial: 0, municipal: 0 });
    const errores = [];
    let totalGuardado = 0;
    let cancelado = false;
    for (const nivelDef of NIVELES) {
      if (cancelarAnalisisRef.current) { cancelado = true; break; }
      try {
        setProgreso(`${nivelDef.label}: consultando base normativa...`);
        const candidatas = await obtenerCandidatas(nivelDef);
        if (candidatas.length === 0) {
          setResultados(r => ({ ...r, [nivelDef.key]: [] }));
          continue;
        }

        const frescas = candidatas.filter(esFresco);
        const pendientes = candidatas.filter(n => !esFresco(n));
        setPendientesMantenimiento(p => ({ ...p, [nivelDef.key]: pendientes.length }));
        let acumulado = [];

        if (frescas.length > 0) {
          const lotesFrescas = chunk(frescas, 20);
          for (let i = 0; i < lotesFrescas.length; i++) {
            if (cancelarAnalisisRef.current) { cancelado = true; break; }
            setProgreso(`${nivelDef.label}: caché ${i + 1}/${lotesFrescas.length} (${lotesFrescas[i].length} normas ya verificadas)...`);
            try {
              const res = await evaluarAplicabilidadLote(nivelDef, lotesFrescas[i]);
              acumulado = [...acumulado, ...res];
              const snapshot = acumulado;
              setResultados(r => ({ ...r, [nivelDef.key]: snapshot }));
              await guardarResultado(nivelDef.key, snapshot);
            } catch (eLote) {
              console.error(`Error evaluando caché tanda ${i + 1} de ${nivelDef.key}:`, eLote);
              errores.push(`${nivelDef.label} — caché tanda ${i + 1}/${lotesFrescas.length}`);
            }
          }
        } else {
          setResultados(r => ({ ...r, [nivelDef.key]: [] }));
        }
        totalGuardado += acumulado.length;
        if (cancelado) break;
      } catch (eNivel) {
        console.error(`Error obteniendo candidatas de ${nivelDef.key}:`, eNivel);
        errores.push(`${nivelDef.label} — consulta a la base`);
      }
    }
    setProgreso("");
    setAnalizando(false);
    setCancelandoAnalisis(false);
    if (cancelado) {
      alert(`Análisis cancelado — se guardó lo generado hasta ahora (${totalGuardado} normas). Podés seguir después apretando el botón de nuevo.`);
    } else if (errores.length > 0) {
      alert(`La matriz se generó, pero estas tandas fallaron (podés apretar el botón de nuevo para reintentar):\n\n${errores.join("\n")}`);
    }
  };

  // Matriz desde base: mismo filtro (rubro + jurisdicción/ubicación) que analizarTodo, pero sin
  // evaluar aplicabilidad puntual — no llama a /api/aria, así que es costo cero real, no solo caché.
  const generarMatrizGratis = async () => {
    if (!empresaSel) return;
    setCargandoGratis(true);
    setResultados({ nacional: null, provincial: null, municipal: null });
    setPendientesMantenimiento({ nacional: 0, provincial: 0, municipal: 0 });
    const errores = [];
    for (const nivelDef of NIVELES) {
      try {
        const candidatas = await obtenerCandidatas(nivelDef);
        const frescas = candidatas.filter(esFresco);
        const nuncaEnriquecidas = candidatas.filter(n => !n.enriquecimiento);
        const vencidas = candidatas.filter(n => n.enriquecimiento && !esFresco(n));
        // Nunca enriquecida ≠ vencida: la primera nunca mintió (siempre se mostró como "Sin evaluar"),
        // la segunda tiene datos reales que podrían leerse como más actuales de lo que son — esa es
        // la única que se excluye y cuenta en el banner de mantenimiento.
        setPendientesMantenimiento(p => ({ ...p, [nivelDef.key]: vencidas.length }));
        const mapeadas = [...frescas, ...nuncaEnriquecidas].map(n => ({
          codigo: n.codigo,
          ...(n.enriquecimiento || {}),
          nombre: n.enriquecimiento?.nombre || n.descripcion,
          aspecto: n.enriquecimiento?.aspecto || n.categoria || "-",
          aplicabilidad: "Sin evaluar",
          riesgo: "Sin evaluar",
        }));
        setResultados(r => ({ ...r, [nivelDef.key]: mapeadas }));
      } catch (eNivel) {
        console.error(`Error obteniendo candidatas de ${nivelDef.key} (matriz gratis):`, eNivel);
        errores.push(nivelDef.label);
      }
    }
    setCargandoGratis(false);
    if (errores.length > 0) {
      alert(`La matriz se generó, pero estos niveles fallaron (podés apretar el botón de nuevo para reintentar):\n\n${errores.join("\n")}`);
    }
  };

  const actualizarVigencias = async () => {
    const todasLasNormas = [
      ...(resultados.nacional || []),
      ...(resultados.provincial || []),
      ...(resultados.municipal || []),
    ];
    if (todasLasNormas.length === 0) {
      alert("Primero generá al menos un nivel (Nacional/Provincial/Municipal) antes de actualizar vigencias.");
      return;
    }
    if (!window.confirm(`Esto va a usar búsqueda web y tiene costo de API real (${todasLasNormas.length} normas). ¿Confirmás?`)) {
      return;
    }
    const controller = new AbortController();
    abortVigenciasRef.current = controller;
    setActualizando(true);
    try {
      const listaTexto = todasLasNormas.map(n => `${n.codigo} | ${n.nombre}`).join("\n");
      const resp = await fetch("/api/aria", {
        method: "POST",
        headers: { ...(await authHeader()), "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 8000,
          system: "Sos un consultor experto en normativa argentina. Respondés SIEMPRE y ÚNICAMENTE con JSON válido.",
          tools: [{ type: "web_search_20250305", name: "web_search", max_uses: 10 }],
          messages: [{ role: "user", content: `Verificá con web_search si cada una de estas normas sigue vigente y si hubo modificaciones posteriores a 2025. Respondé ÚNICAMENTE con JSON válido:

{"normas":[{"codigo":"...","vigente":true,"actualizacion":"texto o vacío"}]}

Normas:
${listaTexto}` }],
        }),
      });
      const respData = await resp.json();
      const texto2 = (respData.content || []).filter(b => b.type === "text").map(b => b.text).join("\n");
      const verificacion = repararJSON(texto2);
      if (!verificacion || !Array.isArray(verificacion.normas)) {
        alert("ARIA no devolvió un JSON válido para la verificación.");
        setActualizando(false);
        return;
      }
      const porCodigo = new Map(verificacion.normas.map(n => [String(n.codigo), n]));

      const aplicarUpdate = (lista) => (lista || []).map(n => {
        const v = porCodigo.get(String(n.codigo));
        if (!v) return n;
        return { ...n, actualizacion: v.vigente === false ? `⚠️ NO VIGENTE (verificado) — ${n.actualizacion || ""}` : (v.actualizacion || n.actualizacion) };
      });

      const nuevoNac = aplicarUpdate(resultados.nacional);
      const nuevoProv = aplicarUpdate(resultados.provincial);
      const nuevoMun = aplicarUpdate(resultados.municipal);
      setResultados({ nacional: nuevoNac, provincial: nuevoProv, municipal: nuevoMun });
      if (nuevoNac) await guardarResultado("nacional", nuevoNac);
      if (nuevoProv) await guardarResultado("provincial", nuevoProv);
      if (nuevoMun) await guardarResultado("municipal", nuevoMun);
    } catch (e) {
      if (e.name === "AbortError") {
        alert("Actualización cancelada — la búsqueda ya iniciada puede haber generado costo igual, esto solo evita usar/esperar el resultado.");
      } else {
        console.error("Error actualizando vigencias:", e);
        alert("Error al actualizar vigencias.");
      }
    } finally {
      setActualizando(false);
      abortVigenciasRef.current = null;
    }
  };

  const consolidarYExportar = async () => {
    if (!resultados.nacional && !resultados.provincial && !resultados.municipal) {
      alert("Generá al menos un nivel antes de exportar.");
      return;
    }
    const wb = new ExcelJS.Workbook();
    wb.creator = "ALQOS™ — LegalMatrix Engine";
    wb.created = new Date();

    const AZUL = "FF7B5BF2", BLANCO = "FFFFFFFF", GRIS_CLARO = "FFF9FAFB";
    const setHeader = (cell, txt) => {
      cell.value = txt;
      cell.font = { bold: true, color: { argb: BLANCO }, size: 10 };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: AZUL } };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    };
    const setCell = (cell, val, bgc) => {
      cell.value = val ?? "";
      cell.font = { size: 9 };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgc || BLANCO } };
      cell.alignment = { vertical: "middle", wrapText: true };
    };

    const COLS = [
      ["Nivel", "nivel", 12],
      ["Código", "codigo", 18], ["Nombre", "nombre", 32], ["Aspecto", "aspecto", 12],
      ["Categoría", "categoria", 14], ["Descripción", "descripcion", 40],
      ["Autoridad", "autoridad_aplicacion", 20], ["Artículo", "articulo_aplicable", 16],
      ["Obligaciones concretas", "obligaciones_concretas", 36], ["Evidencia", "evidencia_objetiva", 28],
      ["Frecuencia", "frecuencia", 14], ["Vencimiento", "vencimiento", 16],
      ["Responsable", "responsable_sugerido", 18], ["Sanciones", "sanciones", 26],
      ["Aplicabilidad", "aplicabilidad", 16], ["Riesgo", "riesgo", 12],
      ["Cláusula ISO", "iso_clausula", 20], ["Actualización", "actualizacion", 24], ["Link", "link", 20],
    ];

    const agregarHojaConsolidada = () => {
      const combinado = [
        ...(resultados.nacional || []).map(n => ({ ...n, nivel: "Nacional" })),
        ...(resultados.provincial || []).map(n => ({ ...n, nivel: "Provincial" })),
        ...(resultados.municipal || []).map(n => ({ ...n, nivel: "Municipal" })),
      ];
      if (combinado.length === 0) return;
      const ws = wb.addWorksheet("Matriz Legal");
      ws.columns = COLS.map(c => ({ width: c[2] }));
      const h = ws.getRow(1);
      COLS.forEach((c, i) => setHeader(h.getCell(i + 1), c[0]));
      combinado.forEach((n, idx) => {
        const row = ws.getRow(idx + 2);
        const bgc = idx % 2 === 0 ? BLANCO : GRIS_CLARO;
        COLS.forEach((c, i) => setCell(row.getCell(i + 1), n[c[1]], bgc));
      });
      ws.views = [{ state: "frozen", ySplit: 1 }];
      ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: COLS.length } };
    };

    const wsRes = wb.addWorksheet("Resumen");
    wsRes.columns = [{ width: 24 }, { width: 60 }];
    const hr = wsRes.getRow(1);
    setHeader(hr.getCell(1), "Dato"); setHeader(hr.getCell(2), "Información");
    [
      ["Empresa", empresaSel.nombre || ""],
      ["Rubro", empresaSel.rubro || ""],
      ["Provincia", empresaSel.provincia || ""],
      ["Municipio", empresaSel.municipio || ""],
      ["Fecha exportación", new Date().toLocaleDateString("es-AR")],
      ["Total Nacional", String((resultados.nacional || []).length)],
      ["Total Provincial", String((resultados.provincial || []).length)],
      ["Total Municipal", String((resultados.municipal || []).length)],
    ].forEach(([k, v], i) => {
      const row = wsRes.getRow(i + 2);
      setCell(row.getCell(1), k, i % 2 === 0 ? BLANCO : GRIS_CLARO);
      setCell(row.getCell(2), v, i % 2 === 0 ? BLANCO : GRIS_CLARO);
    });

    agregarHojaConsolidada();

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `LegalMatrix_${(empresaSel.nombre || "empresa").replace(/\s+/g, "_")}_${new Date().toLocaleDateString("es-AR").replace(/\//g, "-")}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Clasificación única del corpus: Enunciativa vs Obligación concreta ──
  // Se corre una sola vez (o cuando entren normas nuevas sin clasificar). No usa web_search,
  // así que no paga la tarifa de búsqueda — solo lee la descripción y clasifica.
  const EJEMPLOS_CLASIFICACION = `EJEMPLOS YA CLASIFICADOS (de un registro real hecho a mano):

ENUNCIATIVA (declara un principio general, no genera una acción concreta para la empresa):
- "Art. 41° - Todos los habitantes gozan del derecho a un ambiente sano..." (Constitución Nacional)
- "Art. 124° - Corresponde a las provincias el dominio originario de los recursos naturales..."

OBLIGACIÓN CONCRETA (exige que la empresa haga algo puntual: inscribirse, presentar, medir, pagar, tramitar):
- "Ley 11.459 de Radicación Industrial" (exige habilitación industrial)
- "Apruébanse normas operativas para la contratación de seguros previstos por el art. 22 de la Ley 25.675" (exige contratar seguro ambiental)
- "Apruébase los formularios para la presentación de la Declaración Jurada anual" (exige presentar DDJJ)
- "Modificación de la Reglamentación General de la Ley de Tránsito, sustituye artículos" (exige cumplir normas de tránsito)`;

  const clasificarLoteCorpus = async (lote) => {
    const listaTexto = lote.map(n => `${n.codigo} | ${n.descripcion}`).join("\n");
    const resp = await fetch("/api/aria", {
      method: "POST",
      headers: { ...(await authHeader()), "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-6",
        max_tokens: 4000,
        system: "Sos un experto en normativa argentina. Clasificás cada norma como ENUNCIATIVA (declara un principio, no genera acción concreta) u OBLIGACION (exige que la empresa haga algo puntual). Respondés SIEMPRE y ÚNICAMENTE con JSON válido.",
        messages: [{ role: "user", content: `${EJEMPLOS_CLASIFICACION}

Clasificá cada una de estas normas. Respondé ÚNICAMENTE con JSON válido:
{"clasificacion":[{"codigo":"...","enunciativa":true}]}

NORMAS A CLASIFICAR:
${listaTexto}` }],
      }),
    });
    const respData = await resp.json();
    const texto2 = (respData.content || []).filter(b => b.type === "text").map(b => b.text).join("\n");
    const parsed = repararJSON(texto2);
    if (!parsed || !Array.isArray(parsed.clasificacion)) {
      throw new Error("ARIA no devolvió JSON válido para la clasificación.");
    }
    return parsed.clasificacion;
  };

  const clasificarCorpus = async () => {
    setClasificando(true);
    const CLAS_BATCH = 60;
    try {
      setProgresoClasificacion("Buscando normas sin clasificar...");
      const { data: sinClasificar, error } = await supabase
        .from("hs_normativa_base")
        .select("codigo, descripcion")
        .eq("documento_cliente", false)
        .is("es_enunciativa", null);

      if (error) { throw error; }
      if (!sinClasificar || sinClasificar.length === 0) {
        alert("No hay normas pendientes de clasificar — el corpus ya está al día.");
        return;
      }

      const lotes = chunk(sinClasificar, CLAS_BATCH);
      let ok = 0, fallidos = 0;
      for (let i = 0; i < lotes.length; i++) {
        setProgresoClasificacion(`Tanda ${i + 1}/${lotes.length} (${lotes[i].length} normas)...`);
        try {
          const clasif = await clasificarLoteCorpus(lotes[i]);
          const updates = clasif.map(c =>
            supabase.from("hs_normativa_base")
              .update({ es_enunciativa: c.enunciativa === true })
              .eq("codigo", c.codigo)
          );
          await Promise.all(updates);
          ok += clasif.length;
        } catch (eLote) {
          console.error(`Error clasificando tanda ${i + 1}:`, eLote);
          fallidos += lotes[i].length;
        }
      }
      alert(`Clasificación terminada: ${ok} normas clasificadas${fallidos ? `, ${fallidos} quedaron pendientes (volvé a apretar el botón para reintentar solo esas)` : ""}.`);
    } catch (e) {
      console.error("Error en clasificarCorpus:", e);
      alert("Error al clasificar el corpus. Revisá la consola.");
    } finally {
      setProgresoClasificacion("");
      setClasificando(false);
    }
  };

  const totalNormas = (resultados.nacional?.length || 0) + (resultados.provincial?.length || 0) + (resultados.municipal?.length || 0);

  const Nav = ({ titulo, onBack }) => (
    <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", gap: "10px", position: "sticky", top: 0, zIndex: 100, marginBottom: "1.5rem" }}>
      <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Volver</button>
      <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>{titulo}</span>
    </div>
  );

  if (vista === "form") return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Nav titulo={editandoEmpresaLM ? "Editar Empresa" : "Nueva Empresa"} onBack={() => setVista("lista")} />
      <div style={{ padding: "0 1.5rem 2rem", maxWidth: "700px", margin: "0 auto" }}>
        <div style={card}>
          <span style={lbl}>Razón social / Nombre</span>
          <input value={formEmpresaLM.nombre||""} onChange={e=>setFormEmpresaLM(f=>({...f,nombre:e.target.value}))} style={inp}/>

          <span style={lbl}>Rubro</span>
          <select value={formEmpresaLM.rubro||""} onChange={e=>setFormEmpresaLM(f=>({...f,rubro:e.target.value}))} style={inp}>
            {RUBROS_LM.map(r => <option key={r}>{r}</option>)}
          </select>

          <span style={lbl}>Provincia</span>
          <select value={formEmpresaLM.provincia||""} onChange={e=>setFormEmpresaLM(f=>({...f,provincia:e.target.value}))} style={inp}>
            <option value="">Seleccionar...</option>
            {PROVINCIAS_LM.map(p => <option key={p}>{p}</option>)}
          </select>

          <span style={lbl}>Municipio / Partido</span>
          <input value={formEmpresaLM.municipio||""} onChange={e=>setFormEmpresaLM(f=>({...f,municipio:e.target.value}))} style={inp}/>

          <div style={{ marginTop:"20px", paddingTop:"16px", borderTop:`1px solid ${borde}` }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"10px" }}>
              <span style={{ fontSize:"12px", color:subtexto, fontWeight:"600" }}>📍 Sitios / Plantas</span>
              {(formEmpresaLM.sitios||[]).length < 5 && (
                <button type="button" onClick={() => setFormEmpresaLM(f => ({ ...f, sitios:[...(f.sitios||[]), {nombre:"", municipio:"", provincia:"Buenos Aires"}] }))}
                  style={{ padding:"4px 12px", background:"#7B5BF222", border:"1px solid #7B5BF2", borderRadius:"6px", fontSize:"11px", fontWeight:"600", color:"#7B5BF2", cursor:"pointer" }}>
                  + Agregar sitio
                </button>
              )}
            </div>
            {(formEmpresaLM.sitios||[]).length === 0 && (
              <p style={{ fontSize:"11px", color:subtexto, fontStyle:"italic" }}>Sin sitios adicionales. La empresa opera en la ubicación principal.</p>
            )}
            {(formEmpresaLM.sitios||[]).map((sitio, idx) => (
              <div key={idx} style={{ background:oscuro?"#091840":"#f8f7ff", borderRadius:"10px", padding:"12px", marginBottom:"8px", border:`1px solid ${borde}` }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"8px" }}>
                  <span style={{ fontSize:"11px", fontWeight:"700", color:"#7B5BF2" }}>Sitio {idx + 1}</span>
                  <button type="button" onClick={() => setFormEmpresaLM(f => ({ ...f, sitios:(f.sitios||[]).filter((_,i) => i !== idx) }))}
                    style={{ background:"none", border:"none", cursor:"pointer", fontSize:"14px", color:"#e63946" }}>✕</button>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"8px" }}>
                  <div>
                    <span style={{ fontSize:"11px", color:subtexto, fontWeight:"600" }}>Nombre del sitio</span>
                    <input value={sitio.nombre||""} placeholder="Ej: Planta Don Torcuato"
                      onChange={e => setFormEmpresaLM(f => ({ ...f, sitios:(f.sitios||[]).map((s,i) => i===idx ? {...s, nombre:e.target.value} : s) }))}
                      style={{...inp, marginTop:"3px"}} />
                  </div>
                  <div>
                    <span style={{ fontSize:"11px", color:subtexto, fontWeight:"600" }}>Municipio / Partido</span>
                    <input value={sitio.municipio||""} placeholder="Ej: Tigre"
                      onChange={e => setFormEmpresaLM(f => ({ ...f, sitios:(f.sitios||[]).map((s,i) => i===idx ? {...s, municipio:e.target.value} : s) }))}
                      style={{...inp, marginTop:"3px"}} />
                  </div>
                  <div>
                    <span style={{ fontSize:"11px", color:subtexto, fontWeight:"600" }}>Provincia</span>
                    <select value={sitio.provincia||"Buenos Aires"}
                      onChange={e => setFormEmpresaLM(f => ({ ...f, sitios:(f.sitios||[]).map((s,i) => i===idx ? {...s, provincia:e.target.value} : s) }))}
                      style={{...inp, marginTop:"3px"}}>
                      {PROVINCIAS_LM.map(p => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop:"20px", paddingTop:"16px", borderTop:`1px solid ${borde}` }}>
            <span style={{ fontSize:"12px", color:subtexto, fontWeight:"600", display:"block", marginBottom:"8px" }}>⚠️ Aspectos de riesgo (marcá lo que aplique)</span>
            <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>
              {ASPECTOS_RIESGO.map(a => {
                const activo = !!(formEmpresaLM.aspectosRiesgo || {})[a.key];
                return (
                  <button key={a.key} type="button"
                    onClick={() => setFormEmpresaLM(f => ({ ...f, aspectosRiesgo: { ...(f.aspectosRiesgo||{}), [a.key]: !activo } }))}
                    style={{ padding:"6px 12px", borderRadius:"20px", fontSize:"11px", cursor:"pointer", fontWeight:"600",
                      border:activo?"2px solid #7B5BF2":`1px solid ${borde}`,
                      background:activo?"rgba(123,91,242,.12)":cardBg,
                      color:activo?"#7B5BF2":subtexto }}>
                    {a.label}
                  </button>
                );
              })}
            </div>
          </div>

          <button onClick={() => {
            if (editandoEmpresaLM) {
              guardarEmpresasLM(empresas.map(e => e.id === editandoEmpresaLM ? { ...e, ...formEmpresaLM } : e));
            } else {
              const orgIdAuto = (usuario?.rol !== "Administrador" && orgIdsUsuario?.length === 1) ? orgIdsUsuario[0] : null;
              const nueva = { ...formEmpresaLM, id: Math.floor(Date.now()), codigo: `LM-${Math.floor(Date.now())}`, creadoPor: usuario?.nombre||"Sistema", fechaAlta: new Date().toISOString().slice(0,10), organizacion_id: orgIdAuto };
              guardarEmpresasLM([...empresas, nueva]);
            }
            setVista("lista");
          }} style={{ width:"100%", padding:"12px", marginTop:"20px", background:"#7B5BF2", border:"none", borderRadius:"10px", color:"white", fontWeight:"700", cursor:"pointer" }}>
            Guardar
          </button>
        </div>
      </div>
    </div>
  );

  if (!empresaSel) {
    // empresas ya viene filtrada server-side por organizacion_id (ver el useEffect de arriba)
    const filtradas = empresas.filter(e => {
      const q = busqueda.trim().toLowerCase();
      if (!q) return true;
      return (e.nombre || "").toLowerCase().includes(q) || (e.rubro || "").toLowerCase().includes(q);
    });
    return (
      <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
        <Nav titulo="⚖️ LegalMatrix Engine" onBack={onVolver} />
        <div style={{ padding: "0 1.5rem 2rem", maxWidth: "700px", margin: "0 auto" }}>
          {usuario?.rol === "Administrador" && (
            <div style={{ ...card, border: "1px dashed #9ca3af", marginBottom: "16px" }}>
              <p style={{ fontSize: "11px", fontWeight: "700", color: subtexto, margin: "0 0 8px", textTransform: "uppercase", letterSpacing: ".05em" }}>🔧 Admin — mantenimiento del corpus</p>
              <button onClick={clasificarCorpus} disabled={clasificando}
                style={{ width: "100%", padding: "9px", background: clasificando ? "#9ca3af" : "none", border: "1.5px solid #9ca3af", borderRadius: "8px", cursor: clasificando ? "not-allowed" : "pointer", color: clasificando ? "white" : subtexto, fontSize: "11px", fontWeight: "700", marginBottom: "8px" }}>
                {clasificando ? `⏳ ${progresoClasificacion || "Clasificando..."}` : "Clasificar corpus normativo (Enunciativa vs Obligación)"}
              </button>
              <button onClick={mantenimientoCorpus} disabled={mantenimiento}
                style={{ width: "100%", padding: "9px", background: mantenimiento ? "#9ca3af" : "none", border: "1.5px solid #f59e0b", borderRadius: "8px", cursor: mantenimiento ? "not-allowed" : "pointer", color: mantenimiento ? "white" : "#d97706", fontSize: "11px", fontWeight: "700" }}>
                {mantenimiento ? `⏳ ${progresoMantenimiento || "Manteniendo..."}` : "💰 Mantenimiento del corpus (usa web_search, gasta API)"}
              </button>
            </div>
          )}
          <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
            placeholder="🔍 Buscar empresa por nombre o rubro..."
            style={{ width: "100%", padding: "10px 14px", borderRadius: "10px", border: `1px solid ${borde}`, fontSize: "13px", boxSizing: "border-box", marginBottom: "16px", background: cardBg, color: texto }} />
          <button onClick={() => { setEditandoEmpresaLM(null); setFormEmpresaLM({ rubro: "Metalmecánica", sitios: [] }); setVista("form"); }}
            style={{ width: "100%", padding: "10px", marginBottom: "16px", background: "#7B5BF2", border: "none", borderRadius: "10px", color: "white", fontWeight: "700", fontSize: "13px", cursor: "pointer" }}>
            🏢 Datos de Empresa — Nueva
          </button>
          {cargandoEmpresas ? (
            <p style={{ textAlign: "center", color: subtexto, padding: "2rem" }}>Cargando empresas...</p>
          ) : filtradas.length === 0 ? (
            <div style={{ textAlign: "center", padding: "3rem" }}>
              <p style={{ fontSize: "32px" }}>🏭</p>
              <p style={{ color: subtexto, marginTop: "8px" }}>Sin empresas cargadas todavía. Creá la primera con "Datos de Empresa".</p>
            </div>
          ) : filtradas.map(e => (
            <div key={e.id} style={{ ...card, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div onClick={() => seleccionarEmpresa(e)} style={{ cursor: "pointer", flex: 1 }}>
                <p style={{ fontSize: "14px", fontWeight: "700", color: texto, margin: "0 0 3px" }}>{e.nombre}</p>
                <p style={{ fontSize: "12px", color: subtexto, margin: 0 }}>{e.rubro} · {e.municipio || e.localidad}, {e.provincia}</p>
              </div>
              <div style={{ display: "flex", gap: "6px" }}>
                <button onClick={ev => { ev.stopPropagation(); setEditandoEmpresaLM(e.id); setFormEmpresaLM(e); setVista("form"); }}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: "14px" }}>✏️</button>
                <button onClick={ev => { ev.stopPropagation(); if (window.confirm(`¿Eliminar ${e.nombre}?`)) { eliminarDato(STORAGE_EMPRESAS, e.id); setEmpresas(empresas.filter(x => x.id !== e.id)); } }}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: "14px", color: "#e63946" }}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Nav titulo={`⚖️ LegalMatrix — ${empresaSel.nombre}`} onBack={() => setEmpresaSel(null)} />
      <div style={{ padding: "0 1.5rem 2rem", maxWidth: "800px", margin: "0 auto" }}>
        <div style={{ ...card, border: "1.5px solid #7B5BF2" }}>
          <p style={{ fontSize: "12px", color: subtexto, margin: "0 0 14px" }}>
            {empresaSel.rubro} · {empresaSel.municipio || empresaSel.localidad}, {empresaSel.provincia}
          </p>
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={analizarTodo} disabled={analizando || cargandoGratis}
              style={{ flex: 1, padding: "13px", background: analizando ? "#9ca3af" : "#7B5BF2", color: "white", border: "none", borderRadius: "10px", cursor: (analizando || cargandoGratis) ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: "700" }}>
              {analizando ? `⏳ ${progreso || "Analizando..."}` : `⚖️ Generar matriz legal completa ${totalNormas ? `(${totalNormas})` : ""}`}
            </button>
            {analizando && (
              <button onClick={() => { cancelarAnalisisRef.current = true; setCancelandoAnalisis(true); }} disabled={cancelandoAnalisis}
                style={{ padding: "13px 16px", background: "none", border: "1.5px solid #e63946", borderRadius: "10px", cursor: cancelandoAnalisis ? "not-allowed" : "pointer", color: "#e63946", fontSize: "13px", fontWeight: "700" }}>
                {cancelandoAnalisis ? "Cancelando..." : "✕ Cancelar"}
              </button>
            )}
          </div>
          <button onClick={generarMatrizGratis} disabled={analizando || cargandoGratis}
            style={{ width: "100%", marginTop: "8px", padding: "9px", background: "none", border: "1.5px solid #7B5BF2", borderRadius: "10px", cursor: (analizando || cargandoGratis) ? "not-allowed" : "pointer", color: "#7B5BF2", fontSize: "12px", fontWeight: "600" }}>
            {cargandoGratis ? "⏳ Consultando base..." : "📋 Matriz desde base (sin evaluar aplicabilidad, costo cero)"}
          </button>
        </div>

        <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
          <div style={{ display: "flex", gap: "6px", flex: 1 }}>
            <button onClick={actualizarVigencias} disabled={actualizando || totalNormas === 0}
              style={{ flex: 1, padding: "10px", background: actualizando ? "#9ca3af" : "none", border: "1.5px solid #f59e0b", borderRadius: "10px", cursor: (actualizando || totalNormas === 0) ? "not-allowed" : "pointer", color: actualizando ? "white" : "#d97706", fontSize: "12px", fontWeight: "700" }}>
              {actualizando ? "⏳ Verificando vigencias..." : "🔄 Actualizar vigencias"}
            </button>
            {actualizando && (
              <button onClick={() => abortVigenciasRef.current?.abort()}
                style={{ padding: "10px 12px", background: "none", border: "1.5px solid #e63946", borderRadius: "10px", cursor: "pointer", color: "#e63946", fontSize: "12px", fontWeight: "700" }}>
                ✕
              </button>
            )}
          </div>
          <button onClick={consolidarYExportar} disabled={totalNormas === 0}
            style={{ flex: 1, padding: "10px", background: totalNormas === 0 ? "#9ca3af22" : "#22c55e", border: "none", borderRadius: "10px", cursor: totalNormas === 0 ? "not-allowed" : "pointer", color: totalNormas === 0 ? subtexto : "white", fontSize: "12px", fontWeight: "700" }}>
            ⬇️ Consolidar y exportar Excel ({totalNormas})
          </button>
        </div>

        {(pendientesMantenimiento.nacional + pendientesMantenimiento.provincial + pendientesMantenimiento.municipal) > 0 && (
          <div style={{ ...card, border: "1px dashed #f59e0b", background: "transparent" }}>
            <p style={{ fontSize: "11px", color: "#d97706", margin: 0 }}>
              ⚠️ {pendientesMantenimiento.nacional + pendientesMantenimiento.provincial + pendientesMantenimiento.municipal} normas no están incluidas todavía (no tienen mantenimiento reciente) — no se generaron para no gastar sin avisar. {usuario?.rol === "Administrador" ? 'Corré "Mantenimiento del corpus" arriba para incorporarlas.' : "Pedile al Administrador que corra el mantenimiento del corpus."}
            </p>
          </div>
        )}

        {NIVELES.map(n => resultados[n.key] && (
          <div key={n.key} style={{ ...card, borderLeft: `4px solid ${n.color}` }}>
            <p style={{ fontSize: "12px", fontWeight: "700", color: n.color, margin: "0 0 8px" }}>{n.label} — {resultados[n.key].length} normas</p>
            {resultados[n.key].slice(0, 5).map((norma, i) => (
              <div key={i} style={{ padding: "6px 0", borderBottom: `0.5px solid ${borde}`, fontSize: "11px", color: texto }}>
                <strong>{norma.codigo}</strong> — {norma.nombre} <span style={{ color: subtexto }}>({norma.aspecto})</span>
              </div>
            ))}
            {resultados[n.key].length > 5 && <p style={{ fontSize: "10px", color: subtexto, margin: "6px 0 0" }}>...y {resultados[n.key].length - 5} más. Ver completo en el Excel exportado.</p>}
          </div>
        ))}

        {totalNormas === 0 && (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <p style={{ fontSize: "32px" }}>⚖️</p>
            <p style={{ color: subtexto, marginTop: "8px" }}>Apretá "Generar matriz legal completa" para analizar Nacional, Provincial y Municipal juntos.</p>
          </div>
        )}
      </div>
    </div>
  );
}
